/**
 * @file 学校検索フォームに関するストア
 */

import { defineStore } from 'pinia'

const LOCAL_STORAGE_KEY = 'searchForm'

export const useSearchStore = defineStore({
  id: 'search',

  state: () => ({
    /**
     * 学校検索フォームのデータ
     *
     * **⚠ LocalStorageと同期するために、`watch`などを使用して変更を監視し、変更直後に`store()`を呼び出すようにしてください。**
     */
    form: new SearchForm(),
  }),

  getters: {},

  actions: {
    /**
     * 初期化する
     *
     * 最初に実行するようにしてください。
     *
     * 1. LocalStorageからデータを取得
     * 2. 取得したデータの整合性を確認
     * 3. データをストアへ保持
     */
    init() {
      try {
        // LocalStorageからデータを取得。空ならエラーでcatch節へ
        const data = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (!data) throw null

        // データをパース。パース失敗ならエラーでcatch節へ
        const parsed = JSON.parse(data)

        // ストアへ保持
        this.form.init(parsed)
      } catch {
        // 何らかのエラーの場合は、LocalStorageに初期化済みのデータを格納する
        this.store()
      }
    },

    /** 現在のフォームのデータをLocalStorageへ保存する */
    store() {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.form))
    },

    /** フォームを空にする */
    clear() {
      this.form.clear()
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    },
  },
})

interface SearchFormInitData {
  sname?: string
  prefecture?: string | string[]
  shiryouType?: string
  keitou?: string | string[]
  sno?: string | string[]
}

/**
 * 資料請求の検索フォーム
 */
class SearchForm {
  sname: string
  prefecture: string[]
  shiryouType: string
  keitou: string[]
  sno: string[]

  constructor(initData?: SearchFormInitData) {
    this.sname = initData?.sname ?? ''
    this.prefecture =
      typeof initData?.prefecture === 'string' ? initData?.prefecture.split(',') : initData?.prefecture ?? []
    this.shiryouType = initData?.shiryouType ?? ''
    this.keitou = typeof initData?.keitou === 'string' ? initData?.keitou.split(',') : initData?.keitou ?? []
    this.sno = typeof initData?.sno === 'string' ? initData?.sno.split(',') : initData?.sno ?? []
  }

  // TODO: コンストラクタとinitの役割がほぼ同じになってしまっている
  init(initData?: SearchFormInitData) {
    this.sname = initData?.sname ?? ''
    this.prefecture =
      typeof initData?.prefecture === 'string' ? initData?.prefecture.split(',') : initData?.prefecture ?? []
    this.shiryouType = initData?.shiryouType ?? ''
    this.keitou = typeof initData?.keitou === 'string' ? initData?.keitou.split(',') : initData?.keitou ?? []
    this.sno = typeof initData?.sno === 'string' ? initData?.sno.split(',') : initData?.sno ?? []
  }

  clear() {
    this.sname = ''
    this.prefecture = []
    this.shiryouType = ''
    this.keitou = []
    this.sno = []
  }

  /**
   * 検索用のクエリオブジェクト
   *
   * 配列はカンマ区切りの文字列に変換します。
   *
   * e.g.
   * `{ prefecture: '2,12,23', shiryouType: '1', keitou: '101,103' }`
   **/
  get query() {
    return {
      sname: this.sname !== '' ? this.sname : undefined,
      prefecture: this.prefecture.length > 0 ? this.prefecture.join(',') : undefined,
      shiryouType: this.shiryouType !== '' ? this.shiryouType : undefined,
      keitou: this.keitou.length > 0 ? this.keitou.join(',') : undefined,
      sno: this.sno.length > 0 ? this.sno.join(',') : undefined,
    }
  }
}
